import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, imgSrc, BACKEND_URL } from "@/lib/api";
import { fetchTags, clearTagsCache } from "@/lib/tags";

const emptyPost = {
  title: "",
  slug: "",
  body_html: "",
  excerpt: "",
  featured_image: "",
  categories: [],
  tags: [],
  status: "draft",
  publish_date: "",
  phone: "",
  location_url: "",
  website_url: "",
  email: "",
  seo: { meta_title: "", meta_description: "", og_title: "", og_description: "", og_image: "" },
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
};

// Pretty-print HTML so the source view is readable: each block-level tag on its own line.
const BLOCK_TAGS = ["p", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "blockquote", "pre", "figure", "figcaption", "hr", "table", "thead", "tbody", "tr", "td", "th", "div", "iframe"];
function formatHtml(html) {
  if (!html) return "";
  let s = html.replace(/\r?\n/g, "").replace(/&nbsp;/gi, " ").trim();
  BLOCK_TAGS.forEach((tag) => {
    s = s.replace(new RegExp(`<${tag}(\\s[^>]*)?>`, "gi"), (m) => `\n${m}`);
    s = s.replace(new RegExp(`</${tag}>`, "gi"), (m) => `${m}\n`);
  });
  const lines = s.split("\n").map((l) => l.trim()).filter((l) => l.length);
  let depth = 0;
  const out = lines.map((line) => {
    const isClose = /^<\/(ul|ol|blockquote|table|thead|tbody|tr|figure)>/i.test(line);
    if (isClose) depth = Math.max(0, depth - 1);
    const indented = "  ".repeat(depth) + line;
    const isOpen = /^<(ul|ol|blockquote|table|thead|tbody|tr|figure)(\s[^>]*)?>$/i.test(line);
    if (isOpen) depth += 1;
    return indented;
  });
  return out.join("\n");
}

export default function PostEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const [post, setPost] = useState(emptyPost);
  const [showSource, setShowSource] = useState(false);
  const showSourceRef = useRef(false);
  useEffect(() => { showSourceRef.current = showSource; }, [showSource]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Dynamic, owner-managed tags
  const [allTags, setAllTags] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [tagRows, setTagRows] = useState(null); // null = manage panel closed
  const [tagsSaving, setTagsSaving] = useState(false);
  const [tagError, setTagError] = useState("");
  const [newTagName, setNewTagName] = useState("");

  useEffect(() => {
    fetchTags().then(setAllTags);
    api.get("/auth/me").then((r) => setIsOwner(Boolean(r.data?.is_owner))).catch(() => {});
  }, []);

  // Chips show the global list plus any tag already on this post (so nothing is hidden).
  const chipTags = useMemo(() => {
    const list = [...allTags];
    (post.tags || []).forEach((t) => { if (!list.includes(t)) list.push(t); });
    return list;
  }, [allTags, post.tags]);

  const openTagManager = () => {
    setTagError("");
    setNewTagName("");
    setTagRows(allTags.map((t, i) => ({ key: `t${i}-${t}`, original: t, name: t, removed: false })));
  };

  const saveTags = async () => {
    const rows = (tagRows || []).filter((r) => !r.removed && r.name.trim());
    if (!rows.length) return setTagError("At least one tag is required.");
    const renames = {};
    (tagRows || []).forEach((r) => {
      const name = r.name.trim().toLowerCase();
      if (!r.removed && r.original && name && name !== r.original) renames[r.original] = name;
    });
    const tags = [];
    rows.forEach((r) => {
      const name = r.name.trim().toLowerCase();
      if (!tags.includes(name)) tags.push(name);
    });
    setTagsSaving(true);
    setTagError("");
    try {
      await api.put("/admin/tags", { tags, renames });
      clearTagsCache();
      const fresh = await fetchTags(true);
      setAllTags(fresh);
      // Keep this post's selected tags consistent with renames/deletions.
      set("tags", (post.tags || []).map((t) => renames[t] || t).filter((t) => fresh.includes(t)));
      setTagRows(null);
    } catch (e) {
      setTagError(e?.response?.data?.detail || "Failed to save tags.");
    } finally {
      setTagsSaving(false);
    }
  };

  const copyShareLink = async () => {
    // Backend /api/share/{slug} serves live per-post OG tags (updates instantly, no redeploy).
    const link = `${BACKEND_URL}/api/share/${post.slug}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copy this share link:", link);
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/posts/${id}`).then((res) => {
      const p = res.data;
      setPost({
        ...emptyPost,
        ...p,
        // If the user already flipped to source view before this GET resolved,
        // format immediately so the raw server HTML doesn't overwrite the pretty view.
        body_html: showSourceRef.current ? formatHtml(p.body_html) : p.body_html,
        seo: { ...emptyPost.seo, ...(p.seo || {}) },
        publish_date: toDatetimeLocal(p.publish_date),
      });
    });
  }, [id, isEdit]);

  useEffect(() => {
    api.post("/admin/activity/view", { section: isEdit ? "edit_post" : "new_post", detail: id || "" }).catch(() => {});
  }, [id, isEdit]);

  const set = (field, value) => setPost((prev) => ({ ...prev, [field]: value }));
  const setSeo = (field, value) => setPost((prev) => ({ ...prev, seo: { ...prev.seo, [field]: value } }));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data.url; // relative /api/files/...
  };

  const onFeatured = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      set("featured_image", url);
      if (!post.seo.og_image) setSeo("og_image", url);
    } finally {
      setUploading(false);
    }
  };

  const onOgImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setSeo("og_image", url);
    } finally {
      setUploading(false);
    }
  };

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const url = await uploadFile(file);
      const editor = quillRef.current?.getEditor?.();
      if (!editor) return;
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, "image", imgSrc(url));
      editor.setSelection(range.index + 1);
    };
    input.click();
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ["bold", "italic", "underline", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler]
  );

  const save = async (statusOverride) => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: post.title,
        slug: post.slug || undefined,
        body_html: post.body_html,
        excerpt: post.excerpt,
        featured_image: post.featured_image,
        categories: post.categories,
        tags: post.tags,
        status: statusOverride || post.status,
        publish_date: post.publish_date ? new Date(post.publish_date).toISOString() : null,
        phone: post.phone,
        location_url: post.location_url,
        website_url: post.website_url,
        email: post.email,
        seo: post.seo,
      };
      if (isEdit) {
        await api.put(`/admin/posts/${id}`, payload);
      } else {
        await api.post(`/admin/posts`, payload);
      }
      navigate("/admin", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const toggleSource = () => setShowSource((s) => !s);

  // Format the HTML only after Quill has unmounted (source view active), so Quill's
  // async hydration onChange can't overwrite the pretty-printed result.
  useEffect(() => {
    if (showSource) setPost((prev) => ({ ...prev, body_html: formatHtml(prev.body_html) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSource]);

  const csv = (arr) => (arr || []).join(", ");
  const parseCsv = (str) => str.split(",").map((s) => s.trim()).filter(Boolean);

  return (
    <div className="admin" data-testid="post-editor">
      <header className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-brand">
            <Link to="/admin" className="admin-link">&larr; Posts</Link>
            <span> / {isEdit ? "Edit" : "New"} post</span>
          </div>
          <div className="admin-header-actions">
            {isEdit ? (
              <>
                <button className="btn-ghost" onClick={copyShareLink} data-testid="copy-share-btn">{copied ? "Copied!" : "Copy share link"}</button>
                <a href={`/preview/${id}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="preview-btn">Preview</a>
              </>
            ) : null}
            <button className="btn-ghost" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn">
              Save draft
            </button>
            <button className="btn-primary" onClick={() => save("published")} disabled={saving} data-testid="publish-btn">
              {saving ? "Saving…" : "Publish"}
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main editor-grid">
        <div className="editor-col-main">
          {error ? <div className="editor-error" data-testid="editor-error">{error}</div> : null}

          <label className="editor-label">Title</label>
          <input
            className="editor-input editor-title"
            value={post.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Post title"
            data-testid="editor-title"
          />

          <div className="editor-body-head">
            <label className="editor-label">Body</label>
            <button
              type="button"
              className="btn-ghost btn-small"
              onClick={toggleSource}
              data-testid="toggle-html-btn"
            >
              {showSource ? "Visual editor" : "</> HTML"}
            </button>
          </div>

          {showSource ? (
            <textarea
              className="editor-input editor-html"
              value={post.body_html}
              onChange={(e) => set("body_html", e.target.value)}
              data-testid="editor-html"
              spellCheck={false}
            />
          ) : (
            <div className="editor-quill" data-testid="editor-quill">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={post.body_html}
                onChange={(v) => set("body_html", v)}
                modules={modules}
              />
            </div>
          )}
        </div>

        <aside className="editor-col-side">
          <div className="editor-card">
            <label className="editor-label">Status</label>
            <select className="editor-input" value={post.status} onChange={(e) => set("status", e.target.value)} data-testid="editor-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>

            <label className="editor-label">Publish date</label>
            <input
              type="datetime-local"
              className="editor-input"
              value={post.publish_date}
              onChange={(e) => set("publish_date", e.target.value)}
              data-testid="editor-date"
            />

            <label className="editor-label">Slug (URL)</label>
            <input className="editor-input" value={post.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto from title" data-testid="editor-slug" />
          </div>

          <div className="editor-card">
            <label className="editor-label">Featured image</label>
            {post.featured_image ? (
              <div className="editor-featured">
                <img src={imgSrc(post.featured_image)} alt="featured" />
                <button className="btn-ghost btn-small" onClick={() => set("featured_image", "")}>Remove</button>
              </div>
            ) : null}
            <input type="file" accept="image/*" onChange={onFeatured} data-testid="editor-featured-upload" />
            {uploading ? <div className="editor-hint">Uploading…</div> : null}
          </div>

          <div className="editor-card">
            <label className="editor-label">Excerpt (short description)</label>
            <textarea className="editor-input" rows={3} value={post.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="editor-excerpt" />

            <label className="editor-label">Categories (comma separated)</label>
            <input className="editor-input" value={csv(post.categories)} onChange={(e) => set("categories", parseCsv(e.target.value))} data-testid="editor-categories" />

            <label className="editor-label">Tags</label>
            <div className="editor-tag-toggles" data-testid="editor-tags">
              {chipTags.map((tag) => {
                const active = (post.tags || []).includes(tag);
                return (
                  <button
                    type="button"
                    key={tag}
                    className={`editor-tag-toggle${active ? " is-active" : ""}`}
                    onClick={() => set("tags", active ? post.tags.filter((x) => x !== tag) : [...(post.tags || []), tag])}
                    data-testid={`editor-tag-${tag}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            {isOwner && tagRows === null ? (
              <button type="button" className="btn-ghost btn-small editor-manage-tags-btn" onClick={openTagManager} data-testid="manage-tags-btn">
                Manage tags
              </button>
            ) : null}
            {isOwner && tagRows !== null ? (
              <div className="tag-manage-panel" data-testid="tag-manage-panel">
                {tagRows.map((row) => row.removed ? null : (
                  <div className="tag-manage-row" key={row.key}>
                    <input
                      className="editor-input"
                      value={row.name}
                      onChange={(e) => setTagRows((rows) => rows.map((r) => r.key === row.key ? { ...r, name: e.target.value } : r))}
                      data-testid={`tag-row-name-${row.key}`}
                    />
                    <button
                      type="button"
                      className="btn-ghost btn-small"
                      onClick={() => setTagRows((rows) => rows.map((r) => r.key === row.key ? { ...r, removed: true } : r))}
                      data-testid={`tag-row-delete-${row.key}`}
                    >
                      Delete
                    </button>
                  </div>
                ))}
                <div className="tag-manage-row">
                  <input
                    className="editor-input"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="New tag name"
                    data-testid="tag-add-input"
                  />
                  <button
                    type="button"
                    className="btn-ghost btn-small"
                    onClick={() => {
                      const name = newTagName.trim().toLowerCase();
                      if (!name) return;
                      setTagRows((rows) => [...(rows || []), { key: `new-${Date.now()}`, original: "", name, removed: false }]);
                      setNewTagName("");
                    }}
                    data-testid="tag-add-btn"
                  >
                    Add
                  </button>
                </div>
                {tagError ? <div className="editor-hint tag-manage-error" data-testid="tags-save-error">{tagError}</div> : null}
                <div className="tag-manage-actions">
                  <button type="button" className="btn-primary btn-small" onClick={saveTags} disabled={tagsSaving} data-testid="tags-save-btn">
                    {tagsSaving ? "Saving…" : "Save tags"}
                  </button>
                  <button type="button" className="btn-ghost btn-small" onClick={() => setTagRows(null)} data-testid="tags-cancel-btn">
                    Cancel
                  </button>
                </div>
                <div className="editor-hint">Deleting a tag removes it from all posts. Renaming updates all posts.</div>
              </div>
            ) : null}
          </div>

          <div className="editor-card">
            <div className="editor-card-title">Contact &amp; Location</div>
            <label className="editor-label">Phone number</label>
            <input
              className="editor-input"
              type="tel"
              value={post.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+1 555 123 4567"
              data-testid="editor-phone"
            />
            <label className="editor-label">Location URL</label>
            <input
              className="editor-input"
              type="url"
              value={post.location_url}
              onChange={(e) => set("location_url", e.target.value)}
              placeholder="https://maps.google.com/..."
              data-testid="editor-location-url"
            />
            <label className="editor-label">Website URL</label>
            <input
              className="editor-input"
              type="url"
              value={post.website_url}
              onChange={(e) => set("website_url", e.target.value)}
              placeholder="https://example.com"
              data-testid="editor-website-url"
            />
            <label className="editor-label">Email address</label>
            <input
              className="editor-input"
              type="email"
              value={post.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="hello@example.com"
              data-testid="editor-email"
            />
          </div>

          <div className="editor-card">
            <div className="editor-card-title">SEO &amp; Social (OG)</div>
            <label className="editor-label">Meta title</label>
            <input className="editor-input" value={post.seo.meta_title} onChange={(e) => setSeo("meta_title", e.target.value)} data-testid="editor-meta-title" />
            <label className="editor-label">Meta description</label>
            <textarea className="editor-input" rows={2} value={post.seo.meta_description} onChange={(e) => setSeo("meta_description", e.target.value)} data-testid="editor-meta-desc" />
            <label className="editor-label">OG title</label>
            <input className="editor-input" value={post.seo.og_title} onChange={(e) => setSeo("og_title", e.target.value)} data-testid="editor-og-title" />
            <label className="editor-label">OG description</label>
            <textarea className="editor-input" rows={2} value={post.seo.og_description} onChange={(e) => setSeo("og_description", e.target.value)} data-testid="editor-og-desc" />
            <label className="editor-label">OG image</label>
            {post.seo.og_image ? (
              <div className="editor-featured">
                <img src={imgSrc(post.seo.og_image)} alt="og" />
                <button className="btn-ghost btn-small" onClick={() => setSeo("og_image", "")}>Remove</button>
              </div>
            ) : null}
            <input type="file" accept="image/*" onChange={onOgImage} data-testid="editor-og-image-upload" />
            <div className="editor-hint">Leave OG fields empty to fall back to the title / excerpt / featured image.</div>
          </div>
        </aside>
      </main>
    </div>
  );
}
